import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { BrandLine, IconButton, LoadingSkeleton, ToastProvider } from "../ui";
import { Icon } from "./icons";
import { ensureNextFonts } from "../ui/tokens/fonts";
import { useNextT } from "../i18n";
import { LegacySetPasswordModal } from "../bridges/frame";
import { logoUrl } from "../bridges/assets";
import { SideMenu } from "./SideMenu";
import { SideFooter } from "./SideFooter";
import { ImportStatus } from "./ImportStatus";
import { TopBar } from "./TopBar";
import { ViewingAsStrip } from "./ViewingAsStrip";
import { ViewingAsMode } from "./ViewingAsMode";
import { ViewOnlyNotice } from "../access/components/ViewOnlyNotice";
import { useAccessLive } from "../data/realtime";
import { useFrameLiveEvents } from "./useFrameLiveEvents";
import { ReportLauncherProvider } from "../visits/ReportLauncher";
import { ShowMeStrip } from "../help/components/ShowMeStrip";
import { InboxOpenerProvider, InboxTab } from "../approvals/openInbox";
import { NotificationsPanel } from "../approvals/components/NotificationsPanel";
import { useMarkNextFrameMounted } from "./frameMounted";
import s from "./NextFrame.module.scss";
import { MapPrewarm } from "../map/prewarm";

/** The live listeners need the inbox opener (today's pop-up opens the new panel), so they run inside it. */
const FrameLiveEvents = () => { useFrameLiveEvents(); return null; };
/** Access changes live (5A): mounted once here, never per page. */
const AccessLive = () => { useAccessLive(); return null; };

// Desktop/phone switch is 900px (plan + spec); kit components keep their own 759 breakpoint.
/** The new frame (layout F): one brand line; on desktop no top bar, just the menu column (logo, menu, Help / Notifications / account) and the page as a panel; on phones a slim bar and a drawer. Does not wait for Google Maps itself. */
export const NextFrame = ({ mapsReady }: { mapsReady: boolean }) => {
  const { t } = useNextT();
  const phone = useMediaQuery("(max-width: 899px)", { noSsr: true });
  const [drawer, setDrawer] = useState(false);
  // A menu choice waits here until the drawer has finished closing (see SideMenu onChoose).
  const pending = useRef<(() => void) | null>(null);
  const choose = (go: () => void) => { pending.current = go; setDrawer(false); };
  const runPending = () => { const go = pending.current; pending.current = null; go?.(); };
  const drawerHead = useRef<HTMLDivElement>(null);
  // The drawer stays mounted, so focus the Close button each time it has opened (autoFocus would only fire once).
  const focusClose = () => drawerHead.current?.querySelector("button")?.focus();
  useEffect(() => { ensureNextFonts(); }, []);
  useMarkNextFrameMounted();
  const top = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = top.current;
    const host = frame.current;
    if (!el || !host) return;
    // Desktop only: on a phone the strips scroll away with the page (they would take too much of a small screen).
    const set = () => host.style.setProperty("--cy-frame-top", `${window.matchMedia?.("(min-width: 900px)").matches ? el.offsetHeight : 0}px`);
    set();
    window.addEventListener("resize", set);
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(set);
    ro?.observe(el);
    return () => { window.removeEventListener("resize", set); ro?.disconnect(); };
  }, []);
  // The new notifications panel (2D F9): the bells, today's pop-up and the contact's "Review" open it.
  const [inbox, setInbox] = useState<{ open: boolean; tab?: InboxTab; seq: number }>({ open: false, seq: 0 });
  const openInbox = useCallback((tab?: InboxTab) => setInbox((v) => ({ open: true, tab, seq: v.seq + 1 })), []);
  const closeInbox = useCallback(() => setInbox((v) => ({ ...v, open: false })), []);

  return (
    <InboxOpenerProvider value={openInbox}>
      <FrameLiveEvents />
      <AccessLive />
      <div ref={frame} className={`cy-next ${s.frame}`}>
        <a className={s.skip} href="#cy-main">{t("shell.nav.skip")}</a>
        {/* The staff strips stay on screen and the menu column fits under them: the account card and Help are never pushed below the
            fold (walkthrough 2026-10-29, issue 12). Their height is measured into --cy-frame-top. */}
        <div ref={top} className={s.top}>
          <ViewingAsStrip />
          <ViewingAsMode />
        </div>
        <div className={s.line}><BrandLine /></div>
        {phone && <TopBar onOpenMenu={() => setDrawer(true)} />}
        <div className={s.body}>
          {!phone && (
            <div className={s.column}>
              <img className={s.logo} src={logoUrl} alt="ConnectYid" />
              <ImportStatus />
              <SideMenu />
              <SideFooter />
            </div>
          )}
          <main id="cy-main" tabIndex={-1} className={s.content}>
            {/* "This account is view-only right now" (boards 71 G/H, 94): every page of the frame says it, in the same place. */}
            <div className={s.viewOnly}><ViewOnlyNotice /></div>
            {/* The report launcher and the notifications panel need the toasts (useToast) and Google Maps (the report's
                and the review's address search): R17. */}
            {mapsReady ? (
              <ToastProvider>
                <ReportLauncherProvider>
                  <ShowMeStrip />
                  <Outlet />
                  <NotificationsPanel open={inbox.open} onClose={closeInbox} request={inbox} />
                </ReportLauncherProvider>
              </ToastProvider>
            ) : <><LoadingSkeleton rows={6} /><MapPrewarm /></>}
          </main>
        </div>
        {phone && (
          // anchor="left" = inline START: the app theme mirrors MUI anchors in RTL (as SideSheet relies on).
          // keepMounted: the menu (and the modals it owns) stays mounted, so a choice can run after the close.
          <Drawer anchor="left" open={drawer} onClose={() => setDrawer(false)} className="cy-next"
            ModalProps={{ keepMounted: true }} SlideProps={{ onEntered: focusClose, onExited: runPending }}
            PaperProps={{ role: "dialog", "aria-label": t("shell.nav.label"), className: s.drawer } as any}>
            <BrandLine />
            <div className={s.drawerHead} ref={drawerHead}>
              <img className={s.drawerLogo} src={logoUrl} alt="ConnectYid" />
              {/* Focused on open (focusClose); the drawer returns focus to the hamburger on close. */}
              <IconButton label={t("shell.top.close_menu")} icon={<Icon name="close" />} onClick={() => setDrawer(false)} />
            </div>
            <ImportStatus />
            <SideMenu onChoose={choose} />
            <SideFooter onChoose={choose} />
          </Drawer>
        )}
        <LegacySetPasswordModal />
      </div>
    </InboxOpenerProvider>
  );
};
